import React, { useEffect, useRef, useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";

import { FaX } from "react-icons/fa6";
import { PaperAirplaneIcon } from "@heroicons/react/24/solid";

import { computeEstimatedTime } from "./utils/time";

import OSHome from "./components/OSHome/OSHome";
import CodiStatus from "./components/codiStatus/CodiStatus";
import BuilderCard from "../builderCard/builderCard";
import ExperienceSection from "./components/ExperienceSection/ExperienceSection";
import PersonCard from "./components/PersonCard/PersonCard";
import ProjectCard from "./components/ProjectCard/ProjectCard";
import PeopleSearchBox from "./components/PeopleSearchBox/PeopleSearchBox";
import PeopleResultsBlock from "./components/PeopleResultsBlock/PeopleResultsBlock";
import ProjectPreviewCard from "./components/projectPreviewCard/projectPreviewCard";
import ProjectInfoCard from "./components/projectInfoCard/projectInfoCard";
import TagInput from "../../pages/create/components/ProjectInfoForm/TagInput";
import RoleDescriptions from "../../pages/create/components/CreateRoleSection/RoleDescriptions";
import PostProjectImageCard from "../../pages/create/components/PostProjectImageCard/PostProjectImageCard";
import ProjectBanner from "./components/projectBanner/projectBanner";
import BluePrint from "./components/bluePrint/bluePrint";
import Roadmap from "./components/roadmap/roadmap";

import { RoadmapProgress, BUILD_STEPS } from "./components/roadmapProgress/roadmapProgress";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const profilerSteps = [
  "name",
  "location",
  "experience",
  "collaboration",
  "motivation",
  "commitment",
  "energyRhythm",
  "executionPace",
];

const profilerPrompts = {
  name: { question: "What's your first and last name?", options: [] },
  location: { question: "Where are you currently based? (City & Country)", options: [] },
  experience: {
    question: "Have you worked on any projects before?",
    options: ["Yes", "No", "Still Learning"],
  },
  collaboration: {
    question: "What kind of collaboration are you looking for?",
    options: ["Co-founder", "Team Projects", "Freelance", "Mentorship"],
  },
  motivation: {
    question: "What motivates you to build?",
    options: ["Solving Problems", "Recognition", "Learning", "Innovation", "Money"],
  },
  commitment: {
    question: "How much time can you commit each week?",
    options: ["<5 hrs", "5-10 hrs", "10-20 hrs", "Full-time"],
  },
  energyRhythm: {
    question: "When do you feel most productive during the day?",
    options: ["Morning", "Afternoon", "Evening", "Late Night"],
  },
  executionPace: {
    question: "What’s your approach to execution — bursts or long-term?",
    options: ["Quick Bursts", "Steady Consistency", "Depends on Task"],
  },
};

const projectSteps = [
  "title",
  "oneLiner",
  "description",
  "category",
  "tags",
  "roles",
  "image",
];

const projectPrompts = {
  title: { question: "What’s the title of your project?", options: [] },
  oneLiner: { question: "Give me a one-liner pitch for your project.", options: [] },
  description: { question: "Describe your project in a few sentences.", options: [] },
  category: {
    question: "Which category fits best?",
    options: ["Tech", "Art", "Music", "Science", "Content", "Gaming", "Food", "Fashion", "Craft"],
  },
  tags: { question: "Add some tags (comma-separated)", options: [] },
  roles: {
    question: "What roles are you looking for?",
    options: ["Designer", "Engineer", "Product Manager", "Marketer", "Advisor", "Other"],
  },
  image: { question: "Upload or describe an image for your project.", options: [] },
};

const roadmapSteps = ["timeCommitment", "vision", "proofOfLife", "firstSignal", "milestone"];

const roadmapPrompts = {
  timeCommitment: {
    question: "How much time can you give this project each week?",
    options: ["Casual (2–4 hrs)", "Steady (5–10 hrs)", "Full-on (15+ hrs)"],
  },
  vision: {
    question: "What’s the bigger goal you want this project to reach?",
    options: [
      "Launch a working MVP",
      "Build a community of early adopters",
      "Validate the idea with real users",
      "Raise seed funding",
      "Create long-term social impact",
    ],
  },
  proofOfLife: {
    question: "What’s the very first thing you can complete that proves this project is alive?",
    options: [
      "Set up a landing page",
      "Publish a GitHub repo with initial code",
      "Share a design mockup",
      "Collect the first 10 signups",
      "Release a demo video",
    ],
  },
  firstSignal: {
    question: "What’s the first sign from others that will tell you this project is real?",
    options: [
      "Friends or colleagues giving feedback",
      "Strangers signing up",
      "First paying customer",
      "Getting mentioned on social media",
      "Someone asking to collaborate",
    ],
  },
  milestone: {
    question: "What milestone would make you personally proud in the next few weeks or months?",
    options: [
      "Ship v1 of the product",
      "Onboard first 50 users",
      "Secure a pilot with a partner",
      "Hit $1k in revenue",
      "Get featured in a newsletter or blog",
    ],
  },
};


/* ---------- UI bits ---------- */
const CODI_AVATAR_SRC = `${process.env.PUBLIC_URL || ""}/CodiCharacter.png`;

const Avatar = ({ type, src, name }) => {
  if (type === "ai") {
    return (
      <div className="relative h-8 w-8 group">
        {/* glow behind */}
        <span
          className="pointer-events-none absolute -inset-1 rounded-full
                     bg-gradient-to-tr from-fuchsia-400/35 via-sky-400/30 to-emerald-400/35
                     animate-codiGlow"
          aria-hidden
        />
        {/* image floating */}
        <img
          src={CODI_AVATAR_SRC}
          alt="Codi"
          className="relative h-8 w-8 object-contain drop-shadow-sm
                     animate-codiFloat transition-transform duration-300
                     group-hover:scale-105"
        />
      </div>
    );
  }

  if (src) {
    return (
      <img
        src={src}
        alt="you"
        className="h-8 w-8 rounded-full object-cover"
      />
    );
  }

  const initials = (name || "You")
    .split(" ")
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

  return (
    <div className="h-8 w-8 rounded-full bg-gray-200 text-gray-700 grid place-items-center text-xs font-semibold">
      {initials}
    </div>
  );
};

const Chip = ({ children, onClick }) => (
  <button onClick={onClick} className="px-3 py-1.5 rounded-full text-sm border border-gray-200 hover:border-gray-300 bg-white hover:bg-gray-50 shadow-sm transition">
    {children}
  </button>
);

const LocationStep = ({ onSubmit }) => {
  const [country, setCountry] = useState("");
  const [city, setCity] = useState("");
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState("");

  // Map of common country name variations
  const countryVariations = {
    'usa': 'United States',
    'us': 'United States',
    'united states of america': 'United States',
    'america': 'United States',
    'uk': 'United Kingdom',
    'britain': 'United Kingdom',
    'great britain': 'United Kingdom',
    'england': 'United Kingdom',
    'uae': 'United Arab Emirates',
    'emirates': 'United Arab Emirates',
    'south korea': 'Korea',
    'north korea': 'Korea',
  };

  const normalizeCountry = (countryInput) => {
    const lower = countryInput.toLowerCase().trim();
    return countryVariations[lower] || countryInput.trim();
  };

  const verifyLocation = async () => {
    if (!country.trim() || !city.trim()) return;

    setIsVerifying(true);
    setError("");

    try {
      const cityTrimmed = city.trim();
      const countryNormalized = normalizeCountry(country);
      
      // Use Nominatim API to verify the location
      const query = `${cityTrimmed}, ${countryNormalized}`;
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=5&addressdetails=1`,
        {
          headers: {
            'User-Agent': 'Frontera-Codi-App'
          }
        }
      );

      if (!response.ok) {
        throw new Error('API request failed');
      }

      const data = await response.json();
      
      console.log('Nominatim response:', data);

      // Check if we got valid results
      if (Array.isArray(data) && data.length > 0) {
        // Verify that the result is actually a city/town and matches the country
        const validResult = data.find(result => {
          const address = result.address || {};
          const displayName = (result.display_name || '').toLowerCase();
          const resultType = result.type || '';
          const resultClass = result.class || '';
          
          // Only accept results that are actual settlements (cities, towns, villages)
          const isSettlement = 
            resultClass === 'place' || 
            resultClass === 'boundary' ||
            resultType === 'city' ||
            resultType === 'town' ||
            resultType === 'village' ||
            resultType === 'administrative';
          
          // Reject non-settlement types
          if (resultClass === 'railway' || resultClass === 'highway' || resultClass === 'amenity') {
            return false;
          }
          
          // Check if result has a city-level component in its address
          const hasCity = address.city || address.town || address.village || address.municipality;
          
          // Check if the country matches (case-insensitive, checking both normalized and original)
          const countryLower = country.toLowerCase().trim();
          const countryNormalizedLower = countryNormalized.toLowerCase();
          const countryMatches = 
            (address.country && (
              address.country.toLowerCase().includes(countryNormalizedLower) ||
              address.country.toLowerCase().includes(countryLower)
            )) ||
            displayName.includes(countryNormalizedLower) ||
            displayName.includes(countryLower);
          
          // Check if the city name appears in the result (more flexible matching)
          const cityNameLower = cityTrimmed.toLowerCase();
          const cityMatches = 
            displayName.includes(cityNameLower) ||
            (address.city && address.city.toLowerCase().includes(cityNameLower)) ||
            (address.town && address.town.toLowerCase().includes(cityNameLower)) ||
            (address.village && address.village.toLowerCase().includes(cityNameLower));
          
          return isSettlement && hasCity && countryMatches && cityMatches;
        });

        if (validResult) {
          console.log('Location verified:', validResult);
          // Submit with the original user input, not the normalized version
          onSubmit(`${cityTrimmed}, ${countryNormalized}`);
        } else {
          console.log('No valid city/country match found');
          setError("This location doesn't exist! Please enter a real city and country with correct spelling.");
        }
      } else {
        console.log('No results found');
        setError("This location doesn't exist! Please enter a real city and country with correct spelling.");
      }
    } catch (err) {
      console.error("Location verification error:", err);
      setError("Could not verify location. Please check your spelling and try again.");
    } finally {
      setIsVerifying(false);
    }
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm">
      <div className="text-sm font-medium mb-2">Enter your location</div>
      
      {error && (
        <div className="mb-3 p-2 rounded-md bg-red-50 border border-red-200 text-red-700 text-xs">
          {error}
        </div>
      )}
      
      <label className="block text-xs text-gray-600 mb-1">Country</label>
      <input 
        className="w-full mb-3 rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black/10" 
        placeholder="Type your country…" 
        value={country} 
        onChange={(e) => {
          setCountry(e.target.value);
          setError("");
        }}
      />
      
      <label className="block text-xs text-gray-600 mb-1">City</label>
      <input 
        className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black/10" 
        placeholder="Type your city…" 
        value={city} 
        onChange={(e) => {
          setCity(e.target.value);
          setError("");
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" && country.trim() && city.trim() && !isVerifying) {
            verifyLocation();
          }
        }}
      />
      
      <div className="flex justify-end gap-2 mt-3">
        <button 
          onClick={verifyLocation}
          disabled={!country.trim() || !city.trim() || isVerifying} 
          className="px-3 py-2 rounded-md bg-black text-white text-sm disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
        >
          {isVerifying ? (
            <>
              <span className="h-3 w-3 rounded-full border-2 border-white border-t-transparent animate-spin" />
              Verifying...
            </>
          ) : (
            "Confirm location"
          )}
        </button>
      </div>
    </div>
  );
};

const CollaborationStep = ({ onSubmit }) => {
  const [selected, setSelected] = useState([]);
  const [otherOpen, setOtherOpen] = useState(false);
  const [other, setOther] = useState("");

  const options = [
    "Co-founder",
    "Team Projects",
    "Freelance",
    "Mentorship",
    "Solo builder",
    "Open to networking",
    "Part-time contributor",
    "Full-time role",
    "Advisor",
    "Other",
  ];

  const toggle = (opt) => {
    if (opt === "Other") setOtherOpen(true);
    setSelected((prev) =>
      prev.includes(opt) ? prev.filter((x) => x !== opt) : [...prev, opt]
    );
  };

  const confirm = () => {
    const picks = selected.filter((o) => o !== "Other");
    const payload = [...picks, ...(other.trim() ? [other.trim()] : [])];
    onSubmit(payload.length ? payload.join(", ") : "Not sure yet");
  };

  return (
    <div className="rounded-xl border border-gray-200 bg-white p-3 shadow-sm">
      <div className="text-sm font-medium mb-2">What kind of collaboration are you looking for?</div>
      <div className="flex flex-wrap gap-2">
        {options.map((opt) => {
          const active = selected.includes(opt);
          return (
            <button
              key={opt}
              type="button"
              onClick={() => toggle(opt)}
              className={`px-3 py-1.5 rounded-full text-sm border ${
                active ? "border-black bg-black text-white" : "border-gray-200 bg-white hover:bg-gray-50"
              } transition`}
            >
              {opt}
            </button>
          );
        })}
      </div>

      {otherOpen && (
        <div className="mt-3">
          <label className="block text-xs text-gray-600 mb-1">Add your own</label>
          <input
            className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-black/10"
            placeholder="e.g., Getting feedback, Pair programming, Short-term contributor"
            value={other}
            onChange={(e) => setOther(e.target.value)}
          />
        </div>
      )}

      <div className="mt-3 flex justify-end gap-2">
        <button
          type="button"
          onClick={() => onSubmit("Not sure yet")}
          className="px-3 py-2 rounded-md border text-sm hover:bg-gray-50"
        >
          Skip / Not sure
        </button>
        <button
          type="button"
          onClick={confirm}
          className="px-3 py-2 rounded-md bg-black text-white text-sm"
        >
          Confirm
        </button>
      </div>
    </div>
  );
};

/* ---------- Helpers ---------- */
const norm = (s) => (s || "").replace(/\s+/g, " ").trim().toLowerCase();

// Keep in sync with BuilderCard’s parser
const serializeExperience = (items) => `Experience: ${JSON.stringify(items)}`;
const parseExperience = (value) => {
  if (Array.isArray(value)) return value;
  if (typeof value === "string") {
    try {
      return JSON.parse(value.replace(/^Experience:\s*/, ""));
    } catch {
      return [];
    }
  }
  return [];
};

const CodiOS = ({ onClose, initialMode, project }) => {
  const navigate = useNavigate();

  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const [builderCard, setBuilderCard] = useState(null);

  const [loading, setLoading] = useState(false);
  const [isProfiling, setIsProfiling] = useState(true);
  const [currentStep, setCurrentStep] = useState(0);
  const [experienceActive, setExperienceActive] = useState(false);

  const [showLauncher, setShowLauncher] = useState(false);
  const [prepBanner, setPrepBanner] = useState(null); // { variant: 'people'|'projects', title, subtitle }

  const [mode, setMode] = useState(null); // 'people' | 'projects' | 'create' | null

  const [isCreatingProject, setIsCreatingProject] = useState(false);
  const [currentProjectStep, setCurrentProjectStep] = useState(0);
  const [projectDraft, setProjectDraft] = useState({});

  const [peopleMode, setPeopleMode] = useState("auto"); // "auto" = launcher recs, "request" = explicit query
  const [peopleRequests, setPeopleRequests] = useState([]); 
  const [peopleBuckets, setPeopleBuckets] = useState([]); // [{ title, reason, items: [...] }]
  const [peopleLoading, setPeopleLoading] = useState(false);
  const [peopleError, setPeopleError] = useState(null);

  const [roles, setRoles] = useState([]);
  const [roleDescriptions, setRoleDescriptions] = useState({});
  const [roleErrors, setRoleErrors] = useState({});
  const [awaitingCustomRole, setAwaitingCustomRole] = useState(false);

  const [projectBuckets, setProjectBuckets] = useState([]); 
  const [projectLoading, setProjectLoading] = useState(false);
  const [projectError, setProjectError] = useState(null);
  const [showPreview, setShowPreview] = useState(true);

  const [showStatusBelow, setShowStatusBelow] = useState(false);

  const [analyzedProfile, setAnalyzedProfile] = useState(null);
  const [analysisSummary, setAnalysisSummary] = useState("");

  const [analyzedProject, setAnalyzedProject] = useState(null);
  const [projectSummary, setProjectSummary] = useState("");

  const [projectComplete, setProjectComplete] = useState(false);
  const [createdProject, setCreatedProject] = useState(null);
  const [effectiveProject, setEffectiveProject] = useState(null);
  const [showBannerLoading, setShowBannerLoading] = useState(false);

  const [isBuildingRoadmap, setIsBuildingRoadmap] = useState(false);
  const [currentRoadmapStep, setCurrentRoadmapStep] = useState(0);
  const [roadmapDraft, setRoadmapDraft] = useState({});
  const [checkpoints, setCheckpoints] = useState(null);
  const [predictedHours, setPredictedHours] = useState(null);

  // const effectiveProject = createdProject || project;

  const [phases, setPhases] = useState(null);

  const [buildSteps, setBuildSteps] = useState(
    BUILD_STEPS.map(s => ({ ...s, status: "pending", detail: "" }))
  );
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  // run-once guards
  const initProfileCheckRan = useRef(false);
  const startSessionRan = useRef(false);

  useEffect(() => {
    if (project && project.id) {
      setEffectiveProject(project);
      console.log("Effective project set:", project);
    }
  }, [project]);

  useEffect(() => {
    if (!effectiveProject && project?.id) {
      console.log("🔁 Sync fallback: using project prop as effectiveProject");
      setEffectiveProject(project);
    }
  }, [project, effectiveProject]);

  // Fetch full project details if we only got a partial project object
  useEffect(() => {
    const fetchFullProject = async () => {
      if (!project?.id) return;

      // skip if we already have title, description, etc.
      if (project.title) {
        setEffectiveProject(project);
        return;
      }

      const token = localStorage.getItem("token");
      try {
        const res = await fetch(`${BACKEND_URL}/projects/${project.id}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const data = await res.json().catch(() => ({}));
        const full =
          data?.project ||
          data ||
          project; // fallback to original if structure is flat

        if (res.ok && full.title) {
          console.log("🔁 Fetched full project details:", full);
          setEffectiveProject(full);
        } else {
          console.warn("⚠️ Could not fetch complete project data:", data);
        }
      } catch (err) {
        console.error("❌ Failed to fetch full project details:", err);
      }
    };

    fetchFullProject();
  }, [project]);

  // Ensure effectiveProject syncs with createdProject when launched from AdminView
  useEffect(() => {
    if (!effectiveProject && createdProject) {
      console.log("🔗 Syncing effectiveProject with createdProject:", createdProject);
      setEffectiveProject(createdProject);
    }
  }, [createdProject, effectiveProject]);


  /* ---------- Error helpers ---------- */
  const appendAI = (text) => {
    if (!text) return;
    setMessages((prev) => {
      const lastAI = [...prev].reverse().find((m) => m.sender === "ai");
      if (lastAI && norm(lastAI.text) === norm(text)) return prev;
      return [...prev, { sender: "ai", text }];
    });
  };

  const updateAI = (oldText, newText) => {
    setMessages((prev) =>
      prev.map((m) =>
        m.sender === "ai" && norm(m.text) === norm(oldText)
          ? { ...m, text: newText }
          : m
      )
    );
  };


  const makeHttpError = (res, data) => {
    const err = new Error(
      (data && (data.message || data.error || data.detail)) || res.statusText || "Request failed"
    );
    err.status = res.status;
    err.code = data && (data.code || data.errorCode);
    err.raw = data;
    return err;
  };

  const friendlyFromError = (err) => {
    const msg = (err?.message || "").toLowerCase();

    if (err?.status === 401 || /token.*expired|jwt.*expired|unauthorized/.test(msg)) {
      return "🔒 Session expired — please log in again.";
    }
    if (err?.status === 429 || /rate|quota/.test(msg)) {
      return "⏳ I’m being rate-limited. Try again in a moment.";
    }
    if (err?.status === 404) {
      return "🔎 I couldn’t find that endpoint. (404)";
    }
    if (err?.status >= 500) {
      return "🛠️ Server hiccup on my side. Give me a sec and try again.";
    }
    if (/network|failed to fetch|networkerror/i.test(msg)) {
      return "🌐 I couldn’t reach the server. Check your connection and try again.";
    }
    // Default: say the backend message as-is, but with a friendly preface.
    return `⚠️ ${err?.message || "Something went wrong."}`;
  };

  const resetPeopleResults = () => {
    setPeopleBuckets([]);
    setPeopleError(null);
    setPeopleLoading(false);
  };

  // ---------- Roadmap Helpers ----------
  const buildTimeline = (profile, roadmapDraft) => {
    const skill =
      profile?.experience?.length > 2 ? "intermediate" : "novice"; // quick skill heuristic
    const commitment = roadmapDraft.timeCommitment;

    // TODO: Apply multipliers we discussed
    // (Novice+Casual = longer, Experienced+Full-on = shorter)
    // For now return skeleton
    return {
      phases: ["Forge", "Build", "Ship"],
      checkpoints: roadmapDraft,
    };
  };

  const formatTimeline = (timeline) => {
    // Turns roadmap object into human-readable AI message
    // return `📍 Roadmap:\n- Forge → ${timeline.checkpoints.vision}\n- Build → ${timeline.checkpoints.firstSignal}\n- Ship → ${timeline.checkpoints.milestone}`;
  };

  // Utility to expand phases into { weeks, hours }
  const expandPhases = (phases, predictedHours) => {
    const totalWeeks = Object.values(phases).reduce((a, b) => a + b, 0);

    return Object.fromEntries(
      Object.entries(phases).map(([phase, weeks]) => {
        const hours = Math.round((weeks / totalWeeks) * predictedHours);
        return [phase, { weeks, hours }];
      })
    );
  };

  // --- Backend predictor wrapper ---
  async function getPredictedDuration(blueprint, profile, project) {
    const token = localStorage.getItem("token");

    const res = await fetch(`${BACKEND_URL}/codi/roadmap/predict-duration`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ blueprint, profile, project }),
    });

    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data.message || "Failed to predict duration");
    }

    return data.predictedHours;
  }

  // --- Backend predictor wrapper ---
  async function getPredictedDuration(blueprint, profile, project) {
    const token = localStorage.getItem("token");

    const res = await fetch(`${BACKEND_URL}/codi/roadmap/predict-duration`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ blueprint, profile, project }),
    });

    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data.message || "Failed to predict duration");
    }

    return data.predictedHours;
  }

  // --- Backend checkpoints wrapper ---
  async function getCheckpoints(blueprint, profile, project, phases) {
    const token = localStorage.getItem("token");

    const res = await fetch(`${BACKEND_URL}/codi/roadmap/generate-checkpoints`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ blueprint, profile, project, phases }),
    });

    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data.message || "Failed to generate checkpoints");
    }

    return data.checkpoints;
  }

  // Append a human-friendly error into the chat
  const sayError = (err) => appendAI(friendlyFromError(err));

  // map server questions => step index
  const questionIndexMap = useMemo(() => {
    const map = {};
    profilerSteps.forEach((k, i) => {
      const q = profilerPrompts[k]?.question?.trim();
      if (q) map[q] = i;
    });
    return map;
  }, []);

  const syncStepFromReply = useCallback((reply) => {
    if (!reply) return;
    const idx = questionIndexMap[reply.trim()];
    if (typeof idx === "number") setCurrentStep(idx);
  }, [questionIndexMap]);

  // const showPeopleResults = (buckets, title = "Top Matches", subtitle = "Based on your request") => {
  //   setMessages((prev) => [
  //     ...prev,
  //     {
  //       type: "people-results",
  //       title,
  //       subtitle,
  //       buckets,
  //     },
  //   ]);
  // };

  
  /* Auto-grow textarea */
  useEffect(() => {
    if (!textareaRef.current) return;
    textareaRef.current.style.height = "0px";
    const h = textareaRef.current.scrollHeight;
    textareaRef.current.style.height = Math.min(h, 140) + "px";
  }, [input]);

  /* Fetch user */
  useEffect(() => {
    const run = async () => {
      const token = localStorage.getItem("token");
      const email = localStorage.getItem("userEmail");
      if (!token || !email) return;

      const cached = localStorage.getItem("user");
      if (cached) {
        try {
          const parsed = JSON.parse(cached);
          if (parsed?.email) {
            setUser(parsed);
            return;
          }
        } catch {}
      }

      try {
        const res = await fetch(`${BACKEND_URL}/user/${email}`, { headers: { Authorization: `Bearer ${token}` } });
        const data = await res.json();
        if (res.ok && data?.email) {
          localStorage.setItem("user", JSON.stringify(data));
          setUser(data);
        
          console.log("👤 User object fetched:", data);
          console.log("🔑 UID:", data.uid);
        }
      } catch (err) {
        console.error("❌ Failed to fetch user:", err);
      }
    };
    run();
  }, []);

  /* Check profile (guarded) */
  useEffect(() => {
    const run = async () => {
      if (initProfileCheckRan.current) return;
      initProfileCheckRan.current = true;

      const token = localStorage.getItem("token");
      if (!token) return;

      try {
        const res = await fetch(`${BACKEND_URL}/codi/profiler/check`, { headers: { Authorization: `Bearer ${token}` } });
        const data = await res.json();
        if (res.ok && data.exists) {
          // setIsProfiling(false);
          // setBuilderCard(data.builderProfile);
          // setMessages([
          //   { sender: "ai", text: `✅ Welcome back, ${data.builderProfile?.name?.split(" ")[0] || "builder"}!` },
          //   { sender: "ai", text: "How can I help you today?" },
          // ]);
          setIsProfiling(false);
          setBuilderCard(data.builderProfile);
          setMessages([]);          
          setShowLauncher(true);
        } else {
          setIsProfiling(true);
          // first question comes from us (no server yet)
          appendAI(profilerPrompts[profilerSteps[0]].question);
          setCurrentStep(0);
        }
      } catch (err) {
        console.error("❌ Error checking builder profile:", err);
      }
    };
    if (user) run();
  }, [user]); // eslint-disable-line

  /* Reload once after builder profile creation */
useEffect(() => {
  if (!isProfiling && builderCard) {
    const reloaded = sessionStorage.getItem("profileReloaded");
    if (!reloaded) {
      console.log("🔁 Builder profile created — reloading app to rehydrate state...");
      sessionStorage.setItem("profileReloaded", "true");
      window.location.reload();
    }
  }
}, [isProfiling, builderCard]);


  /* Start profiling session (guarded) */
  useEffect(() => {
    const run = async () => {
      if (startSessionRan.current) return;
      startSessionRan.current = true;

      const token = localStorage.getItem("token");
      if (!token) return;
      try {
        const res = await fetch(`${BACKEND_URL}/codi/profiler/start`, { method: "POST", headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) {
          const data = await res.json().catch(() => ({}));
          console.error("❌ Failed to start profiling:", data.message || res.statusText);
        }
      } catch (err) {
        console.error("❌ Error starting profiling:", err);
      }
    };
    if (user && isProfiling) run();
  }, [user, isProfiling]); // eslint-disable-line

  /* Open launcher once profiling is done */
  useEffect(() => {
    if (!isProfiling && builderCard) setShowLauncher(true);
  }, [isProfiling, builderCard]);

  /* Auto-scroll */
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    if (!peopleLoading && peopleBuckets.length > 0) {
      setShowStatusBelow(true);   // flip to show below
    }
  }, [peopleLoading, peopleBuckets]);

  useEffect(() => {
    if (initialMode) {
      setMode(initialMode); 

      if (initialMode === "roadmap") {
        setIsBuildingRoadmap(true);
        setCurrentRoadmapStep(0);
        setRoadmapDraft({});
        setMessages([]);
      }
    }
  }, [initialMode]);


      const analyzeBuilderProfile = async (p) => {
        if (!p?.email) return;
        const targetEmail = p.email;
        const token = localStorage.getItem("token");
        const requesterEmail = localStorage.getItem("userEmail");

        console.log("🔍 Analyzing builder:", targetEmail);

        try {
          const res = await fetch(`${BACKEND_URL}/codi/analyze/user`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({ targetEmail, requesterEmail }),
          });

          const data = await res.json().catch(() => ({}));
          console.log("📡 Analyze response:", data);

          if (!res.ok) throw new Error(data?.message || "Bad response");

          // 🔹 Construct proper name
          const fullName = [p.firstName, p.lastName].filter(Boolean).join(" ") || p.username || p.email;

          // Prefer backend profile, fallback to PersonCard’s data
          let profile = data?.profile || {
            name: fullName,
            firstName: p.firstName || "",
            lastName: p.lastName || "",
            email: p.email,
            profileImage: p.profileImage || null,
            location: p.builderProfile?.location || "",
            collaboration: p.builderProfile?.collaboration || "",
            motivation: p.builderProfile?.motivation || "",
            commitment: p.builderProfile?.commitment || "",
            energyRhythm: p.builderProfile?.energyRhythm || "",
            executionPace: p.builderProfile?.executionPace || "",
            experience: p.builderProfile?.experience || [],
            identity1: "",
            identity2: "",
          };

          // Ensure name is always set
          if (!profile.name) profile.name = fullName;

          setAnalyzedProfile(profile);

          setAnalysisSummary(
            data?.summary?.trim() || "No summary available for this builder."
          );

          setShowPreview(true);
        } catch (err) {
          console.error("❌ analyzeBuilderProfile error:", err);
        }
      };

      const analyzeProject = async (p) => {
        if (!p?.id) return;
        const token = localStorage.getItem("token");

        try {
          const res = await fetch(`${BACKEND_URL}/projects/${p.id}/details`, {
            headers: { Authorization: `Bearer ${token}` },
          });

          const data = await res.json();
          console.log("📡 Project details response:", data);

          if (!res.ok) throw new Error(data?.message || "Failed to fetch project details.");

          setAnalyzedProject(data.project); // show in right column
          setShowPreview(true);
        } catch (err) {
          console.error("❌ analyzeProject error:", err);
        }
      };

      const handleRoadmapAnswer = async (answer) => {
        const stepKey = roadmapSteps[currentRoadmapStep];
        setRoadmapDraft((prev) => ({ ...prev, [stepKey]: answer }));

        const nextStep = currentRoadmapStep + 1;
        if (nextStep < roadmapSteps.length) {
          setCurrentRoadmapStep(nextStep);
          return;
        }

        // 🚀 All roadmap questions answered
        setIsBuildingRoadmap(false);

        const payload = {
          blueprint: {
            timeCommitment: roadmapDraft.timeCommitment,
            vision: roadmapDraft.vision,
            proofOfLife: roadmapDraft.proofOfLife,
            firstSignal: roadmapDraft.firstSignal,
            milestone: roadmapDraft.milestone,
          },
          profile: {
            name: builderCard.name,
            location: builderCard.location,
            collaboration: builderCard.collaboration,
            commitment: builderCard.commitment,
            energyRhythm: builderCard.energyRhythm,
            executionPace: builderCard.executionPace,
            motivation: builderCard.motivation,
            identity: builderCard.identity,
            experience: builderCard.experience,
          },
          project: {
            title: createdProject?.title || effectiveProject?.title,
            oneLiner: createdProject?.oneLiner || effectiveProject?.oneLiner,
            description: createdProject?.description || effectiveProject?.description,
            category: createdProject?.category || effectiveProject?.category,
            tags: createdProject?.tags || effectiveProject?.tags,
          },
        };

        console.group("📤 Info to send backend for Predicted Duration");
        console.log("📘 Blueprint:", payload.blueprint);
        console.log("👤 Profile:", payload.profile);
        console.log("📦 Project:", payload.project);
        console.groupEnd();

        try {
          // Step 1: predict duration
          const loadingText = "⏳ Predicting duration…";
          appendAI(loadingText);

          const predictedHours = await getPredictedDuration(
            payload.blueprint,
            payload.profile,
            payload.project
          );
          setPredictedHours(predictedHours);  // Store in state
          updateAI(loadingText, `✅ Predicted duration is ~${predictedHours} hours.`);

          // Step 2: estimate time in weeks
          const { weeks, weeklyHours } = computeEstimatedTime(
            predictedHours,
            roadmapDraft.timeCommitment
          );
          appendAI(
            `📅 Estimated time to complete this project: ~${weeks} weeks (at ${weeklyHours} hrs/week).`
          );

          // Step 3: request phase breakdown
          const token = localStorage.getItem("token");
          const res = await fetch(`${BACKEND_URL}/codi/roadmap/distribute-timeline`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify({
              blueprint: payload.blueprint,
              profile: payload.profile,
              project: payload.project,
              estimatedWeeks: weeks,
            }),
          });

          const data = await res.json();
          if (!res.ok || !data?.phases) {
            appendAI("⚠️ Couldn’t split time into phases this time.");
            return;
          }

          appendAI(
            `🔹 Timeline breakdown:\n- Forge → ${data.phases.Forge} weeks\n- Build → ${data.phases.Build} weeks\n- Ship → ${data.phases.Ship} weeks`
          );

          // Step 4: expand phases with hours
          const expandedPhases = expandPhases(data.phases, predictedHours);
          setPhases(expandedPhases);  // Store in state
          console.log("📦 Expanded phases:", expandedPhases);

          // // Step 5: generate checkpoints
          // const checkpointLoading = "📍 Generating checkpoints…";
          // appendAI(checkpointLoading);

          try {
            const generated = await getCheckpoints(
              payload.blueprint,
              payload.profile,
              payload.project,
              expandedPhases
            );

            setCheckpoints(generated); // ✅ show in <Roadmap />
          } catch (err) {
            console.error("❌ checkpoint error:", err);
            appendAI("⚠️ Failed to generate checkpoints.");
          }

          // Step 5: draft text fallback timeline
          const draftingText = "🛠 Drafting timeline…";
          appendAI(draftingText);

          const timeline = buildTimeline(builderCard, roadmapDraft, predictedHours);
          updateAI(draftingText, formatTimeline(timeline));
        } catch (err) {
          console.error("❌ Failed to build roadmap:", err);
          appendAI("⚠️ Sorry, I couldn’t build your roadmap this time.");
        }
      };


  const currentKey = profilerSteps[currentStep];
  const hasOptions = isProfiling && profilerPrompts[currentKey]?.options?.length > 0 && !(currentKey === "experience" && experienceActive);

  /* --------- Networking helpers --------- */
  const postProfilerAnswer = async (text) => {
    const token = localStorage.getItem("token");
    const email = user?.email;
    if (!token || !email) {
      const err = new Error("Missing auth");
      err.status = 401;
      throw err;
    }

    const res = await fetch(`${BACKEND_URL}/codi/profiler/answer`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ text, user: { email } }),
    });

    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw makeHttpError(res, data);
    return data; // { reply, done, identity? }
  };

  const refreshProfile = async () => {
    const token = localStorage.getItem("token");
    if (!token) return null;
    const res = await fetch(`${BACKEND_URL}/codi/profiler/check`, { headers: { Authorization: `Bearer ${token}` } });
    const data = await res.json();
    if (res.ok && data?.builderProfile) setBuilderCard(data.builderProfile);
    return data?.builderProfile || null;
  };

  // Single source of truth for saving experiences
  const saveExperiencesToBackend = async (items) => {
    const payload = serializeExperience(items); // "Experience: [...]"
    const data = await postProfilerAnswer(payload); // same pipeline as other steps
    await refreshProfile();                         // pull latest
    setBuilderCard((prev) => ({ ...prev, experience: payload })); // local live update (singular)
    if (data?.reply) {
      appendAI(data.reply);
      syncStepFromReply(data.reply);
    }
    return data;
  };

  /* --------- Handlers --------- */
  const handleGeneralChat = async (prompt) => {
    setLoading(true);
    const token = localStorage.getItem("token");
    try {
      const res = await fetch(`${BACKEND_URL}/codi/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ prompt }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || "Codi failed to respond");
      appendAI(data.reply);
    } catch (err) {
      console.error("❌ Chat error:", err);
      appendAI("⚠️ Codi ran into an issue. Try again in a bit.");
    } finally {
      setLoading(false);
    }
  };

  const showPrep = (variant) => {
    const label = variant === "people" ? "People matching" : "Project matching";
    setPrepBanner({
      variant,
      title: `Tech team is preparing ${label}.`,
      subtitle: variant === "people"
        ? "Role • Skills • Location • Availability"
        : "Domain • Tech stack • Stage • Time commitment",
    });
  };

  // Helper to fetch people recs
  const fetchPeopleRecs = async (filters = {}) => {
    setMode("people");
    setPeopleMode("auto");       // mark as auto mode
    setPeopleBuckets([]);
    const token = localStorage.getItem("token");
    const email = localStorage.getItem("userEmail");

    // Clear old recs right away
    setPeopleBuckets([]);
    setPeopleLoading(true);
    setPeopleError(null);

    try {
      const res = await fetch(`${BACKEND_URL}/codi/recs/people`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ email, filters, limit: 50 }),
      });

      const data = await res.json();
      console.log("👥 People recs response:", data);
      if (!res.ok) throw new Error(data.message || "Failed to load recommendations.");

      setPeopleBuckets(Array.isArray(data.buckets) ? data.buckets : []);
    } catch (err) {
      console.error("❌ recs error:", err);
      setPeopleError(err.message || "Failed to load recommendations.");
    } finally {
      setPeopleLoading(false);
    }
  };

 const findPeopleRecs = async (query) => {
    setMode("people");
    setPeopleMode("request");
    setPrepBanner(null);
    setShowLauncher(false);
    setPeopleLoading(true);
    setPeopleError(null);

    const token = localStorage.getItem("token");
    const email = localStorage.getItem("userEmail");

    try {
      const res = await fetch(`${BACKEND_URL}/codi/find/people`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ email, query }),
      });

      const data = await res.json();
      console.log("🔎 People find response:", data);

      if (!res.ok) throw new Error(data.message || "Failed to load search results.");

      const buckets = (data.buckets || []).map((b) => ({
        id: b.id || "query",
        label: b.label || `Matches for "${query}"`,
        items: (b.items || []).map((itm) => ({
          user: itm.user || itm,
          meta: itm.meta || {},
        })),
      }));

      // ✅ overwrite peopleBuckets so UI shows them
      setPeopleBuckets(buckets);

      // optional: keep history of requests if you want
      setPeopleRequests((prev) => [...prev, { query, buckets }]);
    } catch (err) {
      console.error("❌ findPeople error:", err);

      const msg =
        err.message === "Invalid or nonsense query."
          ? "⚠️ That search doesn’t look valid. Try describing a role, skill, or location."
          : err.message || "Failed to search people.";

        setPeopleError(msg);
        appendAI(msg); // makes Codi say it in chat
    } finally {
      setPeopleLoading(false);
    }
  };

  const fetchProjectRecs = async (filters = {}) => {
    setMode("projects");
    setProjectBuckets([]);
    setProjectLoading(true);
    setProjectError(null);

    const token = localStorage.getItem("token");
    const email = localStorage.getItem("userEmail");

    try {
      const res = await fetch(`${BACKEND_URL}/codi/recs/projects`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ email, filters, limit: 50 }),
      });

      let data;
      try {
        data = await res.json(); // try JSON first
      } catch {
        const text = await res.text(); // fallback if not JSON
        throw new Error(text || "Server returned non-JSON response.");
      }

      console.log("📦 Project recs response:", data);

      if (!res.ok) throw new Error(data.message || "Failed to load project recommendations.");

      setProjectBuckets(Array.isArray(data.buckets) ? data.buckets : []);
    } catch (err) {
      console.error("❌ project recs error:", err);
      setProjectError(err.message || "Failed to load project recommendations.");
    } finally {
      setProjectLoading(false);
    }
  };

  const launchFindPeople = () => {
    setShowLauncher(false);
    setMode("people");
    setMessages([]);
    setPrepBanner({
      variant: "people",
      title: "People matching",
      subtitle: "Role • Skills • Location • Availability",
    });
    fetchPeopleRecs(); 
  };

  const launchFindProjects = () => {
    setShowLauncher(false);
    setMode("projects");
    setMessages([]);
    setPrepBanner({
      variant: "projects",
      title: "Project matching",
      subtitle: "Domain • Stage",
    });
    fetchProjectRecs();
  };
  
  const launchProjectCreation = () => {
    setShowLauncher(false);
    setMode("create");
    setIsCreatingProject(true);
    setCurrentProjectStep(0);
    setProjectDraft({});
    setMessages([]);
    // appendAI(projectPrompts[projectSteps[0]].question);
  };


  const handleAnswer = async (answer) => {
    if (!isProfiling) {
      setMessages((prev) => [...prev, { sender: "user", text: answer }]);
      return handleGeneralChat(answer);
    }

    const stepKey = profilerSteps[currentStep];
    setMessages((prev) => [...prev, { sender: "user", text: answer }]);

    // Experience "Yes" opens the custom UI; do not send "Yes" to backend
    if (stepKey === "experience" && answer.toLowerCase() === "yes") return;

    setLoading(true);
    try {
      const data = await postProfilerAnswer(answer);

      // update current step’s field on the card
      setBuilderCard((prev) => ({
        ...prev,
        [stepKey]: answer,
        name: stepKey === "name" ? answer : (prev?.name || `${user?.firstName || ""} ${user?.lastName || ""}`.trim()),
        identity1: data?.identity?.title || prev?.identity1,
        identity2: data?.identity?.subtitle || prev?.identity2,
      }));

      // SHOW ONLY SERVER REPLY (the next question), then sync our step to it
      if (data?.reply) {
        appendAI(data.reply);
        syncStepFromReply(data.reply);
      }

      // if (data?.done) setIsProfiling(false);
      if (data?.done) {
        setIsProfiling(false);
        setShowLauncher(true);  // jump to OSHome
        setMessages([]);        // blank canvas
      }

      // IMPORTANT: do NOT locally compute/append the next prompt here.
    } catch (err) {
      console.error("❌ Profiler answer failed:", err);
      sayError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleProjectAnswer = (answer) => {
    const stepKey = projectSteps[currentProjectStep];
    setProjectDraft((prev) => ({ ...prev, [stepKey]: answer }));

    const nextStep = currentProjectStep + 1;
    if (nextStep < projectSteps.length) {
      setCurrentProjectStep(nextStep);
    } else {
      setIsCreatingProject(false);
      setProjectComplete(true)
      appendAI("✅ All set! Your project card preview is on the right.");
    }
  };


  const handleProjectTagChip = (tag) => {
    setProjectDraft((prev) => {
      const tags = (prev.tags || "").split(",").map(t => t.trim()).filter(Boolean);
      if (!tags.includes(tag)) tags.push(tag);
      return { ...prev, tags: tags.join(", ") };
    });
  };


  const addRole = (role) => {
    if (role === "Other") {
      setAwaitingCustomRole(true); // show input box
      return;
    }

    if (!roles.includes(role)) {
      setRoles((prev) => [...prev, role]);
      setProjectDraft((prev) => ({
        ...prev,
        openRoles: [...(prev.openRoles || []), { roleTitle: role }],
      }));
    }
  };

  const handleCustomRoleAdd = (roleTitle) => {
    if (!roleTitle.trim() || roles.includes(roleTitle)) return;

    setRoles((prev) => [...prev, roleTitle]);
    setProjectDraft((prev) => ({
      ...prev,
      openRoles: [...(prev.openRoles || []), { roleTitle }],
    }));

    setAwaitingCustomRole(false); // hide input after adding
  };

  const handleRoleDescriptionChange = (role, updates) => {
    setRoleDescriptions((prev) => ({
      ...prev,
      [role]: { ...prev[role], ...updates }, // merge instead of overwrite
    }));

    setProjectDraft((prev) => ({
      ...prev,
      openRoles: (prev.openRoles || []).map((r) =>
        r.roleTitle === role ? { ...r, ...updates } : r
      ),
    }));
  };

  const removeRole = (roleToRemove) => {
    setRoles((prev) => prev.filter((r) => r !== roleToRemove));
    setRoleDescriptions((prev) => {
      const updated = { ...prev };
      delete updated[roleToRemove];
      return updated;
    });
    setProjectDraft((prev) => ({
      ...prev,
      openRoles: (prev.openRoles || []).filter((r) => r.roleTitle !== roleToRemove),
    }));
  };

  const handleOption = (opt) => {
    // Experience → Yes → open ExperienceSection; don't send to backend
    if (isProfiling && currentKey === "experience" && opt.toLowerCase() === "yes") {
      setMessages((prev) => [...prev, { sender: "user", text: opt }]); // show tap once
      appendAI("Great — let’s attach your projects below.");
      setExperienceActive(true);
      return;
    }
    // for all other steps, let handleAnswer add the user message
    handleAnswer(opt);
  };

  const handleFinalProjectSubmit = async () => {
    const ok = window.confirm(
      "Are you sure you're done editing? This will submit your project."
    );
    if (!ok) return;

    if (!projectDraft?.title || !projectDraft?.image) {
      return alert("Project title and image are required.");
    }

    const token = localStorage.getItem("token");
    const email = localStorage.getItem("userEmail");
    if (!token || !email) return alert("Unauthorized: Please log in.");

    let projectImageBase64 = null;

    try {
      // Convert uploaded file to base64
      if (projectDraft._imageFile) {
        const reader = new FileReader();
        reader.readAsDataURL(projectDraft._imageFile);
        await new Promise((resolve) => (reader.onloadend = resolve));
        projectImageBase64 = reader.result.split(",")[1];
      } else if (projectDraft.image?.startsWith("data:")) {
        projectImageBase64 = projectDraft.image.split(",")[1];
      } else {
        return alert("Image is required.");
      }

      const projectData = {
        title: projectDraft.title,
        oneLiner: projectDraft.oneLiner || "",
        description: projectDraft.description || "",
        category: projectDraft.category || "",
        tags: projectDraft.tags || "",
        owner: email,
        image: projectImageBase64,
      };

      setIsLoading(true);

      // Create project
      const res = await fetch(`${BACKEND_URL}/projects/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(projectData),
      });

      const data = await res.json();
      if (!res.ok) throw new Error(data.message);

      // Add roles if any
      if (Array.isArray(projectDraft.openRoles)) {
        for (const role of projectDraft.openRoles) {
          const resRole = await fetch(
            `${BACKEND_URL}/applications/${data.projectId}/add-role`,
            {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({
                roleTitle: role.roleTitle,
                timeCommitment: role.timeCommitment || null,
                skills: role.skills || "",
                description: role.description || "",
                questions: [role.question1 || "", role.question2 || ""].filter(Boolean),
              }),
            }
          );

          const roleData = await resRole.json();
          if (!resRole.ok) throw new Error(roleData.message);
        }
      }

      // Trigger loading state before showing ProjectBanner
      setShowBannerLoading(true);
      setProjectComplete(false);
      setIsCreatingProject(false);

      // ⬇️ Switch into roadmap mode instead of create
      setMode("roadmap");
      setIsBuildingRoadmap(true);
      setCurrentRoadmapStep(0);
      setRoadmapDraft({});
      setMessages([]); 

      localStorage.setItem("justCreatedNoti", projectData.title);

      setTimeout(() => {
        setCreatedProject({ ...projectData, id: data.projectId, image: projectDraft.image });
        setShowBannerLoading(false);
      }, 1800);


    } catch (err) {
      console.error("❌ Project submission error:", err);
      if (err.message.includes("token")) navigate("/login");
      else alert(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTextSend = () => {
    if (!input.trim() || loading) return;

    const text = input.trim();
    setInput("");

    setMessages((prev) => [...prev, { sender: "user", text }]);

    if (mode === "create" && isCreatingProject) {
      if (awaitingCustomRole) {
        // reset the flag
        setAwaitingCustomRole(false);

        // add typed role as a new openRole
        addRole(text);
        return;
      }

      handleProjectAnswer(text);
    } else if (isProfiling) {
      handleAnswer(text);
    } else {
      handleGeneralChat(text);
    }
  };

  const handleFinalRoadmapSubmit = async () => {
    const activeProject = createdProject?.id ? createdProject : effectiveProject;

    console.log("🧱 createdProject:", createdProject);
    console.log("⚡ effectiveProject:", effectiveProject);
    console.log("🎯 Using project:", activeProject);

    if (!activeProject?.id) {
      console.warn("❌ No project id found, cannot create roadmap");
      return false;
    }

    const token = localStorage.getItem("token");
    const email = localStorage.getItem("userEmail") || user?.email; 
    try {
      // Step 1: Create roadmap
      const res = await fetch(`${BACKEND_URL}/codi/roadmap/create`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          projectId: activeProject.id,
          predictedHours,
          phases,
          checkpoints,
        }),
      });

    
      const data = await res.json();
      if (!res.ok) throw new Error(data.message);

      console.log("📦 Roadmap created:", data);

      const idToUse =
  data.projectId || data.id || data.project?.id || activeProject.id;

setCreatedProject({
  ...activeProject,
  id: idToUse,
  image: activeProject.image,
});

setEffectiveProject({
  ...activeProject,
  id: idToUse,
  image: activeProject.image,
});

console.log("✅ Project synced to banner with ID:", idToUse);


      // Step 2: Get UID for owner (using new route)
      const uidRes = await fetch(`${BACKEND_URL}/user/${email}/get-uid`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const uidData = await uidRes.json();
      if (!uidRes.ok || !uidData.uid) throw new Error("Failed to fetch UID");

      console.log("🔑 Owner UID fetched:", uidData.uid);

      // ---------------------------
      // Project Capital Disabled for v1
      // ---------------------------
      // // Step 3: Initialize Capital
      // const capitalRes = await fetch(`${BACKEND_URL}/codi/projects/init-capital`, {
      //   method: "POST",
      //   headers: {
      //     "Content-Type": "application/json",
      //     Authorization: `Bearer ${token}`,
      //   },
      //   body: JSON.stringify({
      //     projectId: createdProject.id,
      //     ownerUid: uidData.uid, // fetched from new route
      //     baseCapital: 100,      // default starting capital
      //   }),
      // });

      // const capitalData = await capitalRes.json();
      // if (!capitalRes.ok) throw new Error(capitalData.message);

      // console.log("💰 Capital initialized:", capitalData);
      // ---------------------------

      return true; // success
    } catch (err) {
      console.error("❌ Roadmap save error:", err);
      appendAI("⚠️ Failed to create roadmap.");
      return false;
    }
  };


  // fetch projects for ExperienceSection
  const fetchUserProjects = async () => {
    const token = localStorage.getItem("token");
    const email = user?.email;
    if (!token || !email) return [];
    const [ownedRes, memberRes] = await Promise.all([
      fetch(`${BACKEND_URL}/projects/user/${encodeURIComponent(email)}`, { headers: { Authorization: `Bearer ${token}` }}),
      fetch(`${BACKEND_URL}/projects/user/${encodeURIComponent(email)}/member`, { headers: { Authorization: `Bearer ${token}` }}),
    ]);
    const owned = await ownedRes.json().catch(() => ({}));
    const member = await memberRes.json().catch(() => ({}));
    return [...(owned.projects || []), ...(member.projects || [])];
  };

  /* --------- Normalize and hydrate --------- */
  useEffect(() => {
    const token = localStorage.getItem("token");
    const email = localStorage.getItem("userEmail");

    // Guard: nothing to do if user or project not ready
    if (!token || !email) return;

    // Ensure user UID is loaded
    const ensureUid = async () => {
      if (user?.uid) return; // already has UID
      try {
        const res = await fetch(`${BACKEND_URL}/user/${encodeURIComponent(email)}/get-uid`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await res.json();
        if (res.ok && data?.uid) {
          console.log("✅ UID normalized:", data.uid);
          setUser((prev) => ({ ...(prev || {}), uid: data.uid }));
        } else {
          console.warn("⚠️ UID fetch failed for user:", email);
        }
      } catch (err) {
        console.error("❌ UID normalization error:", err);
      }
    };

    ensureUid();
  }, [user]);

  /* Normalize project object */
  useEffect(() => {
    // Wait until createdProject or prop project is ready
    if (createdProject && createdProject.id) {
      setEffectiveProject((prev) => ({
        ...prev,
        ...createdProject,
        id: createdProject.id,
        image: createdProject.image || prev?.image,
      }));
    } else if (!effectiveProject && project?.id) {
      setEffectiveProject(project);
    }
  }, [createdProject, project, effectiveProject]);

  // Restore the last created project on reload (for persisted users)
  useEffect(() => {
    const lastProject = localStorage.getItem("lastCreatedProject");
    if (!createdProject && lastProject) {
      try {
        const parsed = JSON.parse(lastProject);
        if (parsed?.id) {
          console.log("🔁 Restored last created project:", parsed);
          setCreatedProject(parsed);
          setEffectiveProject(parsed);
        }
      } catch (err) {
        console.warn("⚠️ Failed to parse saved project:", err);
      }
    }
  }, [createdProject]);

  console.log("createdProject:", createdProject);
  console.log("effectiveProject:", effectiveProject);
  console.log("Banner receives:", createdProject || effectiveProject);

  /* --------- Render --------- */
  return (
    <div className="fixed inset-0 z-[9999] bg-white flex flex-col">
      <div className="flex w-full h-full bg-white relative">
        
        {/* Left: Chat */}
        <div className={`flex flex-col ${showPreview ? "flex-1" : "w-full"} bg-neutral-50`}>
          
          {/* Header */}
          <div className="sticky top-0 z-10">
            <div className="h-[56px] px-4 md:px-6 flex items-center justify-between border-b bg-white/90 backdrop-blur">
              <div className="flex items-center gap-3">
                <div>
                  <div className="text-sm font-semibold">Builder Workspace</div>
                  <div className="text-[11px] text-gray-500">
                    {isProfiling ? "Profile setup" : "Chat"}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  className="text-xs rounded-md border border-gray-200 px-2 py-1 hover:bg-gray-50"
                  onClick={() => setShowPreview(!showPreview)}
                >
                  {showPreview ? "Hide Preview" : "Show Preview"}
                </button>
                <button
                  className="h-8 w-8 grid place-items-center rounded-md hover:bg-gray-100"
                  onClick={() => {
                    const ok = window.confirm(
                      "⚠️ You're in Codi.\nExiting will discard your progress.\n\nAre you sure?"
                    );
                    if (!ok) return;
                    setIsProfiling(false);
                    setMessages([]);
                    onClose();
                  }}
                  aria-label="Close"
                >
                  <FaX className="text-gray-700" />
                </button>
              </div>
            </div>
          </div>

          {/* Scrollable Messages + Steps */}
          <div className="flex-1 overflow-y-auto">
            <div className="px-4 md:px-6 py-4 space-y-4">
              
              {!isProfiling && mode === "people" && (
                <div className="mb-6 space-y-6">
                  
                  <div className="flex justify-end">
                    <button
                      onClick={() => {
                        setMode(null);
                        setShowLauncher(true);
                        setAnalyzedProfile(null);
                        setAnalysisSummary("");
                      }}
                        className="text-xs text-blue-600 hover:underline"
                    >
                      ⬅ Back
                    </button>
                  </div>

                  <CodiStatus
                    variant="people"
                    title="Find builders"
                    subtitle="Search by role, skills, location, availability…"
                    onDismiss={() => setPrepBanner(null)}
                    onSearch={(query) => findPeopleRecs(query)}
                  />

                  {/* AUTO recs */}
                  {peopleBuckets?.length > 0 && (
                    <div className="space-y-8">
                      {peopleBuckets.map((bucket, idx) => (
                        <div key={bucket.id || idx}>
                          {bucket.label && (
                            <div className="mb-2 text-sm font-semibold">{bucket.label}</div>
                          )}
                          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                            {(bucket.items || []).map((itm, i) => (
                              <PersonCard
                                key={`${bucket.id || idx}-${i}`}
                                person={itm.user}
                                meta={itm.meta}
                                onPress={(p) => analyzeBuilderProfile(p)}
                              />
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* REQUEST recs */}
                  {/* {peopleRequests.length > 0 && (
                    <div className="space-y-6">
                      <div className="text-sm font-semibold">Your Requests</div>
                      {peopleRequests.map((req, idx) => (
                        <PeopleResultsBlock
                          key={`request-${idx}`}
                          title={`Your Request: ${req.query}`}
                          subtitle="Matches from your manual search"
                          buckets={req.buckets}
                          onPress={(p) => analyzeBuilderProfile(p.email)}
                        />
                      ))}
                    </div>
                  )} */}
                </div>
              )}

              {!isProfiling && mode === "projects" && (
                <div className="mb-6 space-y-6">
                  <div className="flex justify-end">
                    <button
                      onClick={() => {
                        setMode(null);
                        setShowLauncher(true);
                      }}
                        className="text-xs text-blue-600 hover:underline"
                    >
                      ⬅ Back
                    </button>
                 </div>

                <CodiStatus
                  variant="projects"
                  title="Find projects"
                  subtitle="Domain • Tech stack • Stage • Time commitment"
                  onDismiss={() => setPrepBanner(null)}
                />

                {/* Projects Buckets */}
                {projectBuckets?.length > 0 ? (
                  <div className="space-y-8">
                    {projectBuckets.map((bucket, idx) => (
                      <div key={bucket.id || idx}>
                        {bucket.label && (
                          <div className="mb-2 text-sm font-semibold">{bucket.label}</div>
                        )}
                        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
                          {(bucket.items || []).map((itm, i) => (
                            <ProjectCard
                              key={`${bucket.id || idx}-${i}`}
                              project={itm.project}
                              meta={itm.meta}
                              onView={(p) => {
                                if (!p?.id) {
                                  console.warn("⚠️ Project missing id:", p);
                                  return;
                                }
                                navigate(`/projects/${p.id}/details`);
                              }}
                              onPress={(proj) => analyzeProject(proj)}
                            />
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                    ) : (
                  <div className="text-sm text-gray-500 italic mt-3">
                    No projects found yet.
                  </div>
                )}
              </div>
              )}

              {mode === "create" && (
                <div className="mb-6 space-y-6">
                  {/* Back button */}
                  <div className="flex justify-end">
                    <button
                      onClick={() => {
                        if (isCreatingProject && Object.keys(projectDraft || {}).length > 0) {
                          const ok = window.confirm(
                            "⚠️ You’re still creating a project.\nLeaving now will discard your progress.\n\nDo you want to exit?"
                          );
                          if (!ok) return;
                        }

                        // reset state
                        setMode(null);
                        setShowLauncher(true);
                        setIsCreatingProject(false);
                        setProjectDraft({});
                        setCurrentProjectStep(0);
                        setMessages([]);
                        setAnalyzedProject(null);
                        setProjectSummary("");
                      }}
                      className="text-xs text-blue-600 hover:underline"
                    >
                      ⬅ Back
                    </button>
                  </div>

                  {/* Header */}
                  <CodiStatus
                    variant="create"
                    title="Create a new project"
                    subtitle="Answer step by step, and see your card update live"
                    onDismiss={() => setPrepBanner(null)}
                  />

                  {/* Current step question */}
              {isCreatingProject && (
                <div className="flex items-start gap-3">
                  {/* Codi avatar */}
                  <div className="flex-shrink-0">
                    <div className="relative">
                      <img
                        src={CODI_AVATAR_SRC}
                        alt="Codi"
                        className="h-10 w-10 object-contain animate-codiFloat"
                      />
                      <span className="absolute inset-0 rounded-full bg-fuchsia-400/30 blur-md animate-pulse" />
                    </div>
                  </div>

                  {/* Bubble */}
                  <div className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900 
                                  text-white rounded-2xl px-4 py-3 shadow-lg border border-blue-400/20 
                                  max-w-[80%]">
                    {/* bubble tail */}
                    <div className="absolute -left-2 top-4 w-0 h-0 
                                    border-t-[8px] border-t-transparent 
                                    border-r-[12px] border-r-slate-900 
                                    border-b-[8px] border-b-transparent"></div>

                    <h2 className="text-base font-semibold leading-snug">
                      {projectPrompts[projectSteps[currentProjectStep]].question}
                    </h2>
                  </div>
                </div>
              )}

                  {/* ---- Step-specific UI ---- */}

                  {/* Text input steps: title, oneLiner, description */}
              {isCreatingProject &&
                ["title", "oneLiner", "description"].includes(
                  projectSteps[currentProjectStep]
                ) && (
                  <div className="relative flex items-end gap-2 px-1 animate-fadeIn">
                    {/* User bubble */}
                    <div className="flex-1 relative">
                      <div className="relative bg-gradient-to-r from-sky-50 to-blue-100 
                                      rounded-2xl px-4 py-3 shadow-md border border-blue-200">
                        {/* bubble tail */}
                        <div className="absolute -right-2 bottom-3 w-0 h-0 
                                        border-t-[8px] border-t-transparent 
                                        border-l-[12px] border-l-blue-200 
                                        border-b-[8px] border-b-transparent"></div>

                        <textarea
                          className="w-full resize-none bg-transparent outline-none 
                                    text-sm leading-5 text-gray-800 placeholder:text-gray-400
                                    max-h-[120px]"
                          placeholder="Type your answer here…"
                          rows={projectSteps[currentProjectStep] === "description" ? 4 : 2}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" && !e.shiftKey) {
                              e.preventDefault();
                              if (e.currentTarget.value.trim()) {
                                handleProjectAnswer(e.currentTarget.value.trim());
                                e.currentTarget.value = "";
                              }
                            }
                          }}
                        />
                      </div>
                      <div className="text-[11px] text-gray-500 mt-1 ml-1">
                        Press Enter to submit
                      </div>
                    </div>

                    {/* Send button */}
                    <button
                      onClick={() => {
                        const val = document.querySelector("textarea")?.value.trim();
                        if (val) {
                          handleProjectAnswer(val);
                          document.querySelector("textarea").value = "";
                        }
                      }}
                      className="shrink-0 flex items-center justify-center
                                w-10 h-10 rounded-full bg-gradient-to-br from-sky-400 to-blue-600
                                text-white shadow-md hover:scale-105 active:scale-95
                                transition disabled:opacity-50"
                    >
                      <PaperAirplaneIcon className="h-4 w-4 -rotate-45" />
                    </button>
                  </div>
              )}


                  {/* Category step */}
                  {isCreatingProject && projectSteps[currentProjectStep] === "category" && (
                    <div className="flex flex-wrap gap-2">
                      {projectPrompts.category.options.map((opt) => (
                        <Chip key={opt} onClick={() => handleProjectAnswer(opt)}>
                          {opt}
                        </Chip>
                      ))}
                    </div>
                  )}

                  {/* Tags step */}
                  {isCreatingProject && projectSteps[currentProjectStep] === "tags" && (
                    <div className="space-y-3">
                      <TagInput
                        selectedCategory={projectDraft.category}
                        onTagsChange={(tags) =>
                          setProjectDraft((prev) => ({ ...prev, tags }))
                        }
                      />
                      <div className="flex justify-end">
                        <button
                          onClick={() => {
                            if (projectDraft.tags?.length > 0) {
                              handleProjectAnswer(projectDraft.tags);
                            } else {
                              alert("Please add at least one tag.");
                            }
                          }}
                          className="px-4 py-2 rounded-md bg-black text-white text-sm hover:bg-gray-900"
                        >
                          Done Adding Tags
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Roles step */}
                  {isCreatingProject && projectSteps[currentProjectStep] === "roles" && (
                    <div className="space-y-4">
                      {/* role chips */}
                      <div className="flex flex-wrap gap-2">
                        {projectPrompts.roles.options.map((opt) => (
                          <Chip key={opt} onClick={() => addRole(opt)}>
                            {opt}
                          </Chip>
                        ))}
                      </div>

                      {/* custom role input */}
                      {awaitingCustomRole && (
                        <div className="mt-3">
                          <input
                            type="text"
                            className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                            placeholder="Type a custom role and press Enter"
                            onKeyDown={(e) => {
                              if (e.key === "Enter" && e.currentTarget.value.trim()) {
                                e.preventDefault();
                                handleCustomRoleAdd(e.currentTarget.value.trim());
                                e.currentTarget.value = "";
                              }
                            }}
                          />
                        </div>
                      )}

                      {/* role descriptions */}
                      {roles.length > 0 && (
                        <RoleDescriptions
                          roles={roles}
                          descriptions={roleDescriptions}
                          onChange={handleRoleDescriptionChange}
                          onRemove={removeRole}
                          errors={roleErrors}
                        />
                      )}

                      {/* actions */}
                      <div className="flex justify-between">
                        <button
                          onClick={() => handleProjectAnswer([])} // skip roles
                          className="px-4 py-2 rounded-md border text-sm hover:bg-gray-50"
                        >
                          Skip for now
                        </button>
                        {roles.length > 0 && (
                          <button
                            onClick={() => handleProjectAnswer(roles)}
                            className="px-4 py-2 rounded-md bg-black text-white text-sm hover:bg-gray-900"
                          >
                            Done Adding Roles
                          </button>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Image step */}
                  {isCreatingProject && projectSteps[currentProjectStep] === "image" && (
                    <div className="space-y-4">
                      <PostProjectImageCard
                        onImageUpload={(file) => {
                          if (!file) {
                            setProjectDraft((prev) => ({ ...prev, image: "" }));
                            return;
                          }
                          setProjectDraft((prev) => ({
                            ...prev,
                            image: URL.createObjectURL(file),
                            _imageFile: file,
                          }));
                        }}
                      />
                      <div className="flex justify-end">
                        <button
                          onClick={() => {
                            if (projectDraft.image) handleProjectAnswer(projectDraft.image);
                            else alert("Please upload an image.");
                          }}
                          className="px-4 py-2 rounded-md bg-black text-white text-sm hover:bg-gray-900"
                        >
                          Done Uploading Image
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {mode === "roadmap" && (
                <div className="mb-6 space-y-6">
                  {/* Back + Status header */}
                  <div className="flex justify-end">
                    <button
                      onClick={() => {
                        setMode(null);
                        setShowLauncher(true);
                      }}
                      className="text-xs text-blue-600 hover:underline"
                    >
                      ⬅ Back
                    </button>
                  </div>

                  <CodiStatus
                    variant="roadmap"
                    title="Building Roadmap"
                    subtitle="Your roadmap will be saved and linked to this project."
                    onDismiss={() => setPrepBanner(null)}
                  />

                  {/* Q&A flow */}
                  
                  {!isBuildingRoadmap && checkpoints && (
                    <div className="mt-8 space-y-6">
                      {/* Codi Intro Bubble */}
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0">
                          <div className="relative">
                            <img
                              src={CODI_AVATAR_SRC}
                              alt="Codi"
                              className="h-10 w-10 object-contain animate-codiFloat"
                            />
                            <span className="absolute inset-0 rounded-full bg-fuchsia-400/30 blur-md animate-pulse" />
                          </div>
                        </div>

                        <div className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900 
                                        text-white rounded-2xl px-4 py-3 shadow-lg border border-blue-400/20 
                                        max-w-[80%]">
                          <div className="absolute -left-2 top-4 w-0 h-0 
                                          border-t-[8px] border-t-transparent 
                                          border-r-[12px] border-r-slate-900 
                                          border-b-[8px] border-b-transparent"></div>
                          <h2 className="text-base font-semibold leading-snug">
                            Here’s your roadmap preview
                            Hit Create when ready.
                          </h2>
                        </div>
                      </div>

                      {/* Actual roadmap */}
                      <Roadmap
                        checkpoints={checkpoints}
                        projectTitle={createdProject?.title}
                        category={createdProject?.category}
                        tags={createdProject?.tags}
                        onCreate={async () => {
                          const ok = await handleFinalRoadmapSubmit();
                          if (ok) {
                            setCheckpoints(null);  // destroy roadmap preview
                            appendAI("Roadmap successfully created! You can now access it from the project banner on the right.");
                          }
                        }}
                      />
                    </div>
                  )}

                  {isBuildingRoadmap && (
                    <div className="space-y-6">
                      {/* Codi bubble */}
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0">
                          <div className="relative">
                            <img
                              src={CODI_AVATAR_SRC}
                              alt="Codi"
                              className="h-10 w-10 object-contain animate-codiFloat"
                            />
                            <span className="absolute inset-0 rounded-full bg-fuchsia-400/30 blur-md animate-pulse" />
                          </div>
                        </div>

                        <div className="relative bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900 
                                        text-white rounded-2xl px-4 py-3 shadow-lg border border-blue-400/20 
                                        max-w-[80%]">
                          <div className="absolute -left-2 top-4 w-0 h-0 
                                          border-t-[8px] border-t-transparent 
                                          border-r-[12px] border-r-slate-900 
                                          border-b-[8px] border-b-transparent"></div>
                          <h2 className="text-base font-semibold leading-snug">
                            {roadmapPrompts[roadmapSteps[currentRoadmapStep]].question}
                          </h2>
                        </div>
                      </div>

                      {/* Step-specific UI */}
                      {roadmapSteps[currentRoadmapStep] === "timeCommitment" ? (
                        // Time commitment → chips only
                        <div className="flex flex-wrap gap-2">
                          {roadmapPrompts.timeCommitment.options.map((opt) => (
                            <Chip key={opt} onClick={() => handleRoadmapAnswer(opt)}>
                              {opt}
                            </Chip>
                          ))}
                        </div>
                      ) : (
                        // All other steps → chips + textarea
                        <div className="space-y-3">
                          {/* Suggestion chips */}
                          {roadmapPrompts[roadmapSteps[currentRoadmapStep]].options.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                              {roadmapPrompts[roadmapSteps[currentRoadmapStep]].options.map((opt) => (
                                <Chip key={opt} onClick={() => handleRoadmapAnswer(opt)}>
                                  {opt}
                                </Chip>
                              ))}
                            </div>
                          )}

                          {/* Freeform input */}
                          <div className="relative flex items-end gap-2 px-1 animate-fadeIn">
                            <div className="flex-1 relative">
                              <div className="relative bg-gradient-to-r from-sky-50 to-blue-100 
                                              rounded-2xl px-4 py-3 shadow-md border border-blue-200">
                                <textarea
                                  className="w-full resize-none bg-transparent outline-none 
                                            text-sm leading-5 text-gray-800 placeholder:text-gray-400
                                            max-h-[120px]"
                                  placeholder="Type your answer here…"
                                  rows={3}
                                  onKeyDown={(e) => {
                                    if (e.key === "Enter" && !e.shiftKey) {
                                      e.preventDefault();
                                      if (e.currentTarget.value.trim()) {
                                        handleRoadmapAnswer(e.currentTarget.value.trim());
                                        e.currentTarget.value = "";
                                      }
                                    }
                                  }}
                                />
                              </div>
                              <div className="text-[11px] text-gray-500 mt-1 ml-1">
                                Press Enter to submit
                              </div>
                            </div>

                            <button
                              onClick={() => {
                                const val = document.querySelector("textarea")?.value.trim();
                                if (val) {
                                  handleRoadmapAnswer(val);
                                  document.querySelector("textarea").value = "";
                                }
                              }}
                              className="shrink-0 flex items-center justify-center
                                        w-10 h-10 rounded-full bg-gradient-to-br from-sky-400 to-blue-600
                                        text-white shadow-md hover:scale-105 active:scale-95
                                        transition disabled:opacity-50"
                            >
                              <PaperAirplaneIcon className="h-4 w-4 -rotate-45" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {!isProfiling && showLauncher && mode !== "people" && (
                <OSHome
                  builder={builderCard}
                  onPeople={launchFindPeople}
                  onProjects={launchFindProjects}
                  onCreateProject={launchProjectCreation}
                  onDismiss={() => setShowLauncher(false)}
                />
              )}

              {/* Messages */}
              {messages.map((m, i) =>
                m.type === "options" && m.stepKey !== "roles"? (
                  <div key={i} className="flex flex-wrap gap-2 pt-2">
                    {m.options.map((opt, j) => (
                      <Chip
                        key={j}
                        onClick={() =>
                          m.stepKey === "tags"
                            ? handleProjectTagChip(opt)
                            : handleProjectAnswer(opt)
                        }
                      >
                        {opt}
                      </Chip>
                    ))}
                  </div>
                ) : m.type === "analyze-result" ? (
                  <div
                    key={i}
                    className="p-3 rounded-xl border bg-white shadow-sm"
                  >
                    <div className="text-sm text-gray-800 whitespace-pre-wrap">
                      {m.text}
                    </div>
                  </div>
                ) : (
                  <div
                    key={i}
                    className={`text-sm ${
                      m.sender === "ai"
                        ? "text-gray-800 text-left"
                        : "text-gray-600 text-right" 
                    }`}
                  >
                    {m.text}
                  </div>
                )
              )}

              {isProfiling &&
                profilerPrompts[currentKey]?.options?.length > 0 &&
                currentKey !== "collaboration" &&
                !(currentKey === "experience" && experienceActive) && (
                  <div className="flex flex-wrap gap-2 pt-2">
                    {profilerPrompts[currentKey].options.map((opt, i) => (
                      <Chip key={i} onClick={() => handleOption(opt)}>
                        {opt}
                      </Chip>
                    ))}
                  </div>
                )
              }

              {loading && (
                <div className="flex items-center gap-2 text-sm text-gray-500 pl-1 pt-1">
                  <span className="h-2 w-2 rounded-full bg-gray-300 animate-pulse" />
                  Codi is thinking…
                </div>
              )}

              {isProfiling && currentKey === "location" && (
                <LocationStep onSubmit={(v) => handleAnswer(v)} />
              )}
              {isProfiling && currentKey === "collaboration" && (
                <CollaborationStep onSubmit={(v) => handleAnswer(v)} />
              )}
              {isProfiling && currentKey === "experience" && experienceActive && (
                <ExperienceSection
                  userEmail={user?.email}
                  authToken={localStorage.getItem("token")}
                  initial={parseExperience(builderCard?.experience)}
                  fetchUserProjects={fetchUserProjects}
                  onSave={async (items) => {
                    await saveExperiencesToBackend(items);
                  }}
                  onStepComplete={() => setExperienceActive(false)}
                />
              )}
              <div ref={messagesEndRef} />
            </div>
          </div>

          {/* Composer */}
          {!(isProfiling &&
            (currentKey === "location" ||
              currentKey === "collaboration" ||
              (currentKey === "experience" && experienceActive))) &&
            !["people", "projects", "roadmap"].includes(mode) && (
              <div className="border-t bg-white px-3 md:px-4 py-3">
                {/* Disable textarea if project creation is active */}
                {mode === "create" && isCreatingProject ? (
                  <div className="text-sm text-gray-500 italic px-2 py-3">
                    {["image"].includes(projectSteps[currentProjectStep])
                      ? "Upload a project image above ⬆️"
                      : ["category", "roles", "tags"].includes(projectSteps[currentProjectStep])
                      ? "Choose from the options above ⬆️"
                      : ["title", "oneLiner", "description"].includes(
                          projectSteps[currentProjectStep]
                        )
                      ? "Type your answer in the input shown above ⬆️"
                      : ""}
                  </div>
                ) : (
                  <div className="flex items-center gap-2 rounded-xl border border-gray-200 bg-neutral-50 focus-within:border-gray-300 transition p-2">
                    <textarea
                      ref={textareaRef}
                      rows={1}
                      className="w-full resize-none bg-transparent outline-none text-sm px-2 py-2 leading-5 placeholder:text-gray-400"
                      placeholder={
                        isProfiling
                          ? "Answer Codi’s question…"
                          : "Ask Codi anything…"
                      }
                      value={input}
                      onChange={(e) => setInput(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" && !e.shiftKey) {
                          e.preventDefault();
                          handleTextSend();
                        }
                      }}
                      disabled={loading}
                      style={{ minHeight: 40 }}
                    />
                    <button
                      onClick={handleTextSend}
                      disabled={loading || !input.trim()}
                      className="shrink-0 inline-flex items-center gap-2 rounded-lg bg-black text-white text-sm font-medium px-3 py-2 hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <PaperAirplaneIcon className="h-4 w-4 -rotate-45" />
                      Send
                    </button>
                  </div>
                )}

                {isProfiling &&
                  !(profilerPrompts[currentKey]?.options?.length > 0) && (
                    <div className="text-[11px] text-gray-500 mt-2 px-1">
                      Shift+Enter for newline • Enter to send
                    </div>
                  )}
              </div>
          )}


        </div>

        {/* Right: Preview */}
        {showPreview && (
          <div className="hidden md:flex w-[360px] shrink-0 border-l bg-white">
            <div className="w-full h-full overflow-y-auto p-4 space-y-4">
              <div className="mb-3">
                <div className="text-xs uppercase tracking-wide text-gray-500 mb-2">
                  {analyzedProject
                    ? "Project Info"
                    : mode === "create" && Object.keys(projectDraft || {}).length > 0
                    ? "Your Project Draft"
                    : analyzedProfile
                    ? "Analyzed Builder"
                    : Object.keys(builderCard || {}).length > 0
                    ? "Your Builder Profile"
                    : ""}
                </div>
              </div>

              {/* Show Project Creation Draft only when it has content */}
              {showBannerLoading ? (
                // loader while Codi prepares the banner
                <div className="mt-6 animate-pulse">
                  <div className="h-40 rounded-2xl bg-gradient-to-r from-blue-100 via-sky-100 to-blue-200 mb-3" />
                  <div className="h-6 w-3/4 bg-gray-200 rounded mb-2" />
                  <div className="h-4 w-1/2 bg-gray-200 rounded" />
                </div>
              ) :
              (createdProject || effectiveProject) ? (

                // Show banner and bluePrint if a project was just created
                <div className="mt-6 space-y-4">
                  <ProjectBanner
                    project={{
                      ...effectiveProject,
                      ...createdProject, // createdProject overwrites but doesn’t erase id
                      id: createdProject?.id || effectiveProject?.id,
                    }}
                  />

                  <BluePrint draft={roadmapDraft} />
                </div>
              ) : mode === "create" && Object.keys(projectDraft || {}).length > 0 ? (
                <>
                  <ProjectPreviewCard
                    project={projectDraft}
                    editable={true}
                    onEdit={(updates) =>
                    setProjectDraft((prev) => ({ ...prev, ...updates }))
                  }
                />

                  {/* Submit button shows only when project is complete */}
                  {projectComplete && (
                    <div className="mt-4">
                      <button
                        onClick={handleFinalProjectSubmit}
                        className="w-full px-4 py-2 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 transition"
                      >
                        Submit Project
                      </button>
                    </div>
                  )}
                  <div className="rounded-xl border border-gray-100 bg-neutral-50/60 p-3 text-[12px] text-gray-600">
                    Live project card updates as you answer Codi’s questions.
                  </div>
                </>
              ) : analyzedProject ? (
                <>
                  <ProjectInfoCard project={analyzedProject} />
                  <button
                    onClick={() => setAnalyzedProject(null)}
                    className="mt-3 text-xs text-blue-600 hover:underline"
                  >
                    ⬅ Back
                  </button>
                </>
              ) : analyzedProfile ? (
                <>
                  <BuilderCard
                    profile={analyzedProfile}
                    identity={[analyzedProfile.identity1, analyzedProfile.identity2]}
                    profileImage={analyzedProfile.profileImage}
                  />
                  {analysisSummary && (
                    <div className="mt-3 p-3 rounded-xl border border-gray-200 bg-neutral-50 text-sm text-gray-700 whitespace-pre-wrap">
                      {analysisSummary}
                    </div>
                  )}
                  <button
                    onClick={() => {
                      setAnalyzedProfile(null);
                      setAnalysisSummary("");
                    }}
                    className="mt-3 text-xs text-blue-600 hover:underline"
                  >
                    ⬅ Back
                  </button>
                </>
              ) : Object.keys(builderCard || {}).length > 0 ? (
                <>
                  <BuilderCard
                    profile={builderCard}
                    identity={[builderCard.identity1, builderCard.identity2]}
                    profileImage={user?.profileImage}
                  />
                  <div className="rounded-xl border border-gray-100 bg-neutral-50/60 p-3 text-[12px] text-gray-600">
                    Live card updates as you answer questions.
                  </div>
                </>
              ) : (
                <div className="mt-4 space-y-3">
                  <div className="h-36 rounded-xl bg-neutral-100 animate-pulse" />
                  <div className="h-28 rounded-xl bg-neutral-100 animate-pulse" />
                </div>
              )}
            </div>
          </div>
        )}

      </div>
    </div>
  );

};

export default CodiOS;
